import React, { useState, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { translate } from '../../lib/translations';

interface TradePlayersTableProps {
  data: any[];
  onAddToTrade: (player: any) => void;
  language: string;
  theme: string;
  isUserTeam: boolean;
}

type SortConfig = {
  field: string;
  direction: 'asc' | 'desc';
};

export default function TradePlayersTable({
  data,
  onAddToTrade,
  language,
  theme,
  isUserTeam
}: TradePlayersTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'points', direction: 'desc' });
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});
  const lang = language as 'fr' | 'en';

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({ ...prev, [playerId]: true }));
  };

  const handleSort = (field: string) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (field: string) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown size={14} className="ms-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} className="ms-1" />
      : <ArrowDown size={14} className="ms-1" />;
  };

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      let aVal: any = a[sortConfig.field];
      let bVal: any = b[sortConfig.field];

      // Gérer le cas spécial du nom
      if (sortConfig.field === 'name') {
        aVal = (a.skaterfullname || '').toLowerCase();
        bVal = (b.skaterfullname || '').toLowerCase();
      }

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === 'asc'
        ? (aVal || 0) - (bVal || 0)
        : (bVal || 0) - (aVal || 0);
    });
    return sorted;
  }, [data, sortConfig]);

  const formatCurrency = (amount: number, isCompact: boolean = false) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: isCompact ? 'compact' : 'standard'
    }).format(amount);
  };

  const getStatusBadge = (player: any) => {
    if (player.is_scratch) {
      return (
        <span className="badge bg-danger">
          <span className="d-none d-md-inline">{translate('scratch.player', lang)}</span>
          <span className="d-md-none">S</span>
        </span>
      );
    }
    if (player.isnhl) {
      return (
        <span className="badge bg-success">
          <span className="d-none d-md-inline">{translate('main.club', lang)}</span>
          <span className="d-md-none">NHL</span>
        </span>
      );
    }
    return (
      <span className="badge bg-warning">
        <span className="d-none d-md-inline">{translate('reserves', lang)}</span>
        <span className="d-md-none">AHL</span>
      </span>
    );
  };

  const renderSalaryCell = (player: any) => {
    const hasRetention = player.salary_retention_received_pct && player.salary_retention_received_pct > 0;
    const retainedAmount = player.salary_retention_received_amount || 0;
    const effectiveSalary = hasRetention ? player.salary - retainedAmount : player.salary;

    if (hasRetention) {
      return (
        <div className="d-flex align-items-center gap-1 flex-wrap">
          <span className="text-success fw-bold">
            <span className="d-none d-md-inline">{formatCurrency(effectiveSalary)}</span>
            <span className="d-md-none">{formatCurrency(effectiveSalary, true)}</span>
          </span>
          <span 
            className="badge bg-warning text-dark" 
            style={{ fontSize: '0.7rem', cursor: 'help' }}
            title={`${translate('salary.retention.tooltip', lang)
              .replace('{original}', formatCurrency(player.salary))
              .replace('{pct}', player.salary_retention_received_pct)
              .replace('{effective}', formatCurrency(effectiveSalary))}`}
          >
            🔒 -{player.salary_retention_received_pct}%
          </span>
        </div>
      );
    }

    return (
      <>
        <span className="d-none d-md-inline">{formatCurrency(player.salary)}</span>
        <span className="d-md-none">{formatCurrency(player.salary, true)}</span>
      </>
    );
  };

  if (data.length === 0) {
    return (
      <div className="text-center py-4 text-muted">
        <p className="mb-0">{translate('no.players', lang)}</p>
      </div>
    );
  }

  return (
    <div className="table-responsive trade-grid-container">
      <table className="table table-hover table-sm mb-0">
        <thead className={theme === 'dark' ? 'table-dark' : 'table-light'}>
          <tr>
            <th style={{ width: '60px', textAlign: 'center', padding: '0.3rem 0.4rem' }}></th>
            <th style={{ width: '35px', padding: '0.3rem 0.4rem' }}>{translate('trade.players.table.column.photo', lang)}</th>
            <th style={{ minWidth: '110px', cursor: 'pointer', padding: '0.3rem 0.4rem' }} onClick={() => handleSort('name')}>
              {translate('trade.players.table.column.name', lang)}
              {renderSortIcon('name')}
            </th>
            <th style={{ width: '35px', cursor: 'pointer', padding: '0.3rem 0.4rem', textAlign: 'center' }} onClick={() => handleSort('positioncode')}>
              {translate('trade.players.table.column.position', lang)}
              {renderSortIcon('positioncode')}
            </th>
            <th style={{ width: '50px', cursor: 'pointer', textAlign: 'center', padding: '0.3rem 0.4rem' }} onClick={() => handleSort('goals')}>
              <span className="d-none d-md-inline">{translate('trade.players.table.column.goals', lang)}</span>
              <span className="d-md-none">B</span>
              {renderSortIcon('goals')}
            </th>
            <th style={{ width: '50px', cursor: 'pointer', textAlign: 'center', padding: '0.3rem 0.4rem' }} onClick={() => handleSort('assists')}>
              <span className="d-none d-md-inline">{translate('trade.players.table.column.assists', lang)}</span>
              <span className="d-md-none">A</span>
              {renderSortIcon('assists')}
            </th>
            <th style={{ width: '50px', cursor: 'pointer', textAlign: 'center', padding: '0.3rem 0.4rem' }} onClick={() => handleSort('points')}>
              <span className="d-none d-md-inline">{translate('trade.players.table.column.points', lang)}</span>
              <span className="d-md-none">P</span>
              {renderSortIcon('points')}
            </th>
            <th style={{ minWidth: '100px', cursor: 'pointer', padding: '0.3rem 0.4rem' }} onClick={() => handleSort('salary')}>
              {translate('trade.players.table.column.salary', lang)}
              {renderSortIcon('salary')}
            </th>
            <th style={{ width: '70px', padding: '0.3rem 0.4rem' }}>{translate('trade.players.table.column.status', lang)}</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((player) => (
            <tr key={player.playerid}>
              <td className="text-center" style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle' }}>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onAddToTrade(player)}
                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                >
                  +
                </button>
              </td>
              <td style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle' }}>
                {imageErrors[player.playerid] ? (
                  <div 
                    className="player-avatar-fallback d-flex align-items-center justify-content-center text-white fw-bold"
                    style={{ 
                      width: '28px', 
                      height: '28px', 
                      borderRadius: '50%',
                      backgroundColor: '#6c757d',
                      fontSize: '0.7rem'
                    }}
                  >
                    {player.skaterfullname?.[0] || '?'}
                  </div>
                ) : (
                  <img 
                    src={`https://assets.nhle.com/mugs/nhl/20242025/${player.teamabbrevs}/${player.playerid}.png`}
                    onError={() => handleImageError(player.playerid)}
                    alt={player.skaterfullname}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                )}
              </td>
              <td style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle' }}>
                <div className="fw-bold" style={{ fontSize: '0.8rem', lineHeight: '1.1' }}>
                  {player.skaterfullname}
                </div>
                {player.teamabbrevs && (
                  <div className="text-muted" style={{ fontSize: '0.65rem', lineHeight: '1.1' }}>
                    {player.teamabbrevs}
                  </div>
                )}
              </td>
              <td className="text-center" style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle', fontSize: '0.8rem' }}>{player.positioncode}</td>
              <td className="text-center" style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle', fontSize: '0.8rem' }}>{player.goals || 0}</td>
              <td className="text-center" style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle', fontSize: '0.8rem' }}>{player.assists || 0}</td>
              <td className="text-center fw-bold" style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle', fontSize: '0.8rem' }}>{player.points || 0}</td>
              <td style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle', fontSize: '0.8rem' }}>{renderSalaryCell(player)}</td>
              <td style={{ padding: '0.3rem 0.4rem', verticalAlign: 'middle' }}>{getStatusBadge(player)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
